import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Icon, Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import confettiIcon from 'images/icons/confetti.png';
import hotsaleIcon from 'images/icons/hotsale.svg';
import vivaIcon from 'images/brands/etn/vivaIcon.svg';
import discountGFA from 'images/brands-unique/gfa/early-discount.png';
import discountETN from 'images/brands-unique/etn/discountETN.png';
import { useTranslation } from 'react-i18next';
import { formatPercent } from 'utils/Helpers';
import {
  hasUrgencyDiscountAmount,
  isResultDiscountUIType,
  isUrgencyDiscountType,
} from 'utils/urgencyDiscount';
import WalletPrice from './WalletPrice';
import DynamicPricingDiscount from './DynamicPricingDiscount';

const earlyDiscountIcons = {
  etn: discountETN,
  costaline: discountETN,
  gho: discountETN,
  gfa: discountGFA,
};

/**
 * Renders the NewPriceComponent.
 *
 * @param {Object} props - The component props.
 * @param {number} props.total - The total price.
 * @param {number} props.walletTotal - The total price in wallet points.
 * @param {boolean} props.isHighligth - Indicates if the price should be highlighted.
 * @param {string} props.walletType - The type of points.
 * @param {boolean} props.extraWalletText - Indicates if there is a reward ticket.
 * @param {boolean} props.showWalletTotal - Indicates if the wallet total should be shown.
 * @param {boolean} props.isProvider - Indicates if the price is for a provider.
 * @param {boolean} props.useDiscountCurrency - Indicates if the price should use the discount color.
 * @param {node} props.secondaryPrice - The secondary price.
 * @param {boolean} props.lighterText - Indicates if the text should be lighter.
 * @param {boolean} props.showFromLabel - Indicates if the label should be shown.
 * @param {number} props.tripAvailability - Indicates the available seats for the trip.
 * @param {boolean} props.lowAvailability - Indicates low availability seats.
 * @param {boolean} props.hasHotSaleDiscount - Indicates if the trip has a Hot Sale discount.
 * @param {Object} props.providerDiscount - Provider discount information.
 * @param {string} props.discountType - Discount type from pricing.
 * @param {number} props.totalBeforeDiscount - Total price before discount.
 * @param {number} props.discountAvailability - Seats available at the discounted price.
 * @param {string} props.discountExpiresAt - Discount expiration timestamp.
 * @param {boolean} props.showDiscountUrgency - Indicates if discount urgency UI is enabled.
 * @param {boolean} props.providerAnniversary - Indicates if the provider anniversary discount icon should be shown.
 * @param {boolean} props.isViajaAhorrando - Indicates if the Viaja Ahorrando price display should be shown.
 * @param {number} props.originalPrice - Original price shown crossed out for Viaja Ahorrando.
 * @returns {JSX.Element} The rendered component.
 */
const NewPriceComponent = ({
  total,
  walletTotal,
  isHighligth,
  walletType,
  extraWalletText,
  showWalletTotal,
  isProvider,
  useDiscountCurrency,
  secondaryPrice,
  lighterText,
  showFromLabel,
  tripAvailability,
  lowAvailability,
  hasHotSaleDiscount,
  providerDiscount,
  discountType,
  totalBeforeDiscount,
  discountAvailability,
  discountExpiresAt,
  showDiscountUrgency,
  providerAnniversary,
  isViajaAhorrando,
  originalPrice,
}) => {
  const { t } = useTranslation('purchase');
  const { brand } = useWhitelabelEnvs();

  const defaultColor = isHighligth ? 'primary ' : 'grayStrong';
  const defaultCurrencyColor = useDiscountCurrency ? 'discount' : defaultColor;
  const defaultPriceColor = lighterText ? 'grayLight' : defaultCurrencyColor;
  const hasProviderDiscount = hasUrgencyDiscountAmount(providerDiscount);
  const hasResultDiscountUIType = isResultDiscountUIType(discountType);
  const hasUrgencyDiscountType = isUrgencyDiscountType(discountType);
  const resolvedDiscountAvailability =
    discountAvailability ?? providerDiscount?.availability ?? null;
  let discountPercent = null;

  if (hasProviderDiscount) {
    if (providerDiscount.percent) {
      discountPercent = `-${providerDiscount.percent}%`;
    } else {
      discountPercent = `-${formatPercent(
        totalBeforeDiscount || total + providerDiscount.amount,
        total,
      )}`;
    }
  }

  const showDiscountUI = hasProviderDiscount && hasResultDiscountUIType && showDiscountUrgency;
  const discountIcon =
    (hasUrgencyDiscountType && earlyDiscountIcons[brand]) ||
    (hasHotSaleDiscount && hotsaleIcon) ||
    (providerAnniversary && confettiIcon) ||
    null;

  if (isProvider)
    return (
      <Spacing
        size="S"
        responsiveSize="XXS"
        isResponsive
        alignItems="center"
        mobileAlign="flex-end"
      >
        <Badge type="gradient" alphaBg="M" roundCorners smallPadding>
          <Text size="XS" mobileSize="XXS" color="white">
            {t('label.open_ticket_adult_price')}
          </Text>
        </Badge>
        <LocalizedCurrency
          price={total}
          color="primary"
          size="XL"
          mobileSize="L"
          weight="bold"
          currencyStyle="currency"
        />
      </Spacing>
    );

  if (showWalletTotal)
    return (
      <WalletPrice
        walletType={walletType}
        walletTotal={walletTotal}
        extraWalletText={extraWalletText}
      />
    );

  // Viaja Ahorrando case: original price crossed out + Viva price with icon
  if (isViajaAhorrando)
    return (
      <div className="fade-in animated">
        <Spacing vertical alignItems="flex-end" size="XS">
          <LocalizedCurrency
            price={originalPrice || total}
            color="grayMedium"
            size="S"
            currencyStyle="currency"
            lineThrough
          />

          <Spacing alignItems="baseline" justifyContent="flex-end" size="XS">
            <Icon size="M" type={vivaIcon} />
            <LocalizedCurrency
              price={total}
              color={defaultPriceColor}
              weight="bold"
              size="XL"
              mobileSize="L"
              currencyStyle="currency"
            />
          </Spacing>
        </Spacing>
      </div>
    );

  if (showDiscountUI) {
    return (
      <DynamicPricingDiscount
        total={total}
        discountPercent={discountPercent}
        discountAvailability={resolvedDiscountAvailability}
        expiresAt={discountExpiresAt}
        priceColor={defaultCurrencyColor}
        lighterText={lighterText}
        showFromLabel={showFromLabel}
        discountIcon={discountIcon}
        secondaryPrice={secondaryPrice}
      />
    );
  }

  return (
    <div className="fade-in animated">
      <Spacing vertical alignItems="flex-end" size="XS">
        <Spacing alignItems="baseline" size="XS">
          {showFromLabel && (
            <Text size="S" italic>
              {t('general:from')}
            </Text>
          )}
          {hasHotSaleDiscount && <Icon type={hotsaleIcon} size="M" mobileSize="S" />}
          {providerAnniversary && <Icon type={confettiIcon} size="M" mobileSize="S" />}
          <LocalizedCurrency
            price={total}
            color={defaultPriceColor}
            weight="bold"
            size="XL"
            mobileSize="L"
            currencyStyle="currency"
          />
        </Spacing>
        {secondaryPrice}
        {lowAvailability && (
          <Text size="S" color="discount" italic weight="semibold">
            {t('search:available_seats', { count: tripAvailability })}
          </Text>
        )}
      </Spacing>
    </div>
  );
};

NewPriceComponent.propTypes = {
  total: PropTypes.number,
  isHighligth: PropTypes.bool,
  extraWalletText: PropTypes.bool,
  walletTotal: PropTypes.number,
  walletType: PropTypes.string,
  showWalletTotal: PropTypes.string,
  isProvider: PropTypes.bool,
  useDiscountCurrency: PropTypes.bool,
  secondaryPrice: PropTypes.node,
  lighterText: PropTypes.bool,
  showFromLabel: PropTypes.bool,
  lowAvailability: PropTypes.bool,
  tripAvailability: PropTypes.number,
  hasHotSaleDiscount: PropTypes.bool,
  providerDiscount: PropTypes.shape({
    amount: PropTypes.number,
    percent: PropTypes.number,
    availability: PropTypes.number,
  }),
  discountType: PropTypes.string,
  totalBeforeDiscount: PropTypes.number,
  discountAvailability: PropTypes.number,
  discountExpiresAt: PropTypes.string,
  showDiscountUrgency: PropTypes.bool,
  providerAnniversary: PropTypes.bool,
  isViajaAhorrando: PropTypes.bool,
  originalPrice: PropTypes.number,
};

NewPriceComponent.defaultProps = {
  discountExpiresAt: null,
  showDiscountUrgency: false,
  providerAnniversary: false,
  isViajaAhorrando: false,
  originalPrice: null,
};

export default NewPriceComponent;
